import React from "react";
import { FiChevronDown, FiChevronUp, FiEye, FiPlus, FiPlusCircle } from "react-icons/fi";
import type { All8PageContent } from "@apa/core-renderers";
import type { ComponentDefinition, Path } from "../../Pages/pageComponentLibrary";
import { getComponentRuntimeKey } from "./pageEditorUtils";
import PageEditorFieldControl from "./PageEditorFieldControl";

type PageEditorComponentsPanelProps = {
	editable: boolean;
	previewComponents: All8PageContent;
	componentDefinitionMap: Map<string, ComponentDefinition>;
	activeComponentKey: string | null;
	setActiveComponentKey: React.Dispatch<React.SetStateAction<string | null>>;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	removeComponent: (index: number) => void;
	moveComponent: (from: number, to: number) => void;
	jsonDrafts: Record<string, string>;
	setJsonDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
	jsonErrors: Record<string, string>;
	setJsonErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
	onOpenLibrary: () => void;
	/** When provided, empty state shows "Start with the page header" button that adds the Page Header component. */
	onAddPageHeader?: () => void;
	allowImageVariables?: boolean;
};

/** Page components editing panel with reordering, deletion and per-field editing. */
const PageEditorComponentsPanel: React.FC<PageEditorComponentsPanelProps> = ({
	editable,
	previewComponents,
	componentDefinitionMap,
	activeComponentKey,
	setActiveComponentKey,
	updateComponentValue,
	removeComponent,
	moveComponent,
	jsonDrafts,
	setJsonDrafts,
	jsonErrors,
	setJsonErrors,
	onOpenLibrary,
	onAddPageHeader,
	allowImageVariables
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
		<div className="flex flex-wrap items-center justify-between gap-3">
			<div>
				<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Components</p>
				<p className="text-sm text-slate-600">
					Build the page with reusable blocks. Edits instantly feed the live preview.
				</p>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					onClick={onOpenLibrary}
					disabled={!editable}
					className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiPlusCircle /> Add component
				</button>
				<button
					type="button"
					onClick={onOpenLibrary}
					disabled={!editable}
					className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiEye /> Visual library
				</button>
			</div>
		</div>

		{previewComponents.length === 0 && (
			<div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-600">
				<p className="text-sm font-semibold">No components yet.</p>
				<p className="text-sm text-slate-500">
					{editable ? "Open the library to add a page header, hero, news, stats, or any block." : "Enable editing to add components."}
				</p>
				{editable && (
					<div className="mt-3 flex flex-wrap items-center justify-center gap-2">
						{onAddPageHeader && (
							<button
								type="button"
								onClick={onAddPageHeader}
								className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
							>
								<FiPlus /> Start with the page header
							</button>
						)}
						<button
							type="button"
							onClick={onOpenLibrary}
							className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
						>
							<FiPlusCircle /> Add component
						</button>
					</div>
				)}
			</div>
		)}

		<div className="space-y-3">
			{previewComponents.map((component, index) => {
				const definition = componentDefinitionMap.get(component.componentType as any) ?? null;
				const componentKey = getComponentRuntimeKey(component.componentType as string, index);
				const isOpen = activeComponentKey === componentKey;
				const jsonDraft = jsonDrafts[componentKey] ?? JSON.stringify(component.content ?? {}, null, 2);
				return (
					<div key={componentKey} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
						<div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
							<div className="min-w-0">
								<p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
									{definition?.title ?? component.componentType}
								</p>
								<p className="text-sm text-slate-600 line-clamp-2">
									{definition?.description ?? "Custom component"}
								</p>
							</div>
							<div className="flex flex-wrap items-center gap-2">
								{editable && (
									<>
										<button
											type="button"
											onClick={() => moveComponent(index, Math.max(0, index - 1))}
											disabled={index === 0}
											className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
										>
											Move up
										</button>
										<button
											type="button"
											onClick={() => moveComponent(index, Math.min(previewComponents.length - 1, index + 1))}
											disabled={index === previewComponents.length - 1}
											className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
										>
											Move down
										</button>
										<button
											type="button"
											onClick={() => removeComponent(index)}
											className="rounded-full border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
										>
											Remove
										</button>
									</>
								)}
								<button
									type="button"
									onClick={() =>
										setActiveComponentKey((current) => (current === componentKey ? null : componentKey))
									}
									className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
									aria-expanded={isOpen}
								>
									{isOpen ? <FiChevronUp /> : <FiChevronDown />}
								</button>
							</div>
						</div>
						{isOpen && (
							<div className="space-y-3 p-4">
								<div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-500">
									Type: {component.componentType}
								</div>
								{definition ? (
									<div className="space-y-4">
										{definition.fields.map((field, fieldIndex) => (
											<div key={`${componentKey}-field-${fieldIndex}`}>
												<PageEditorFieldControl
													editable={editable}
													field={field}
													componentIndex={index}
													previewComponents={previewComponents}
													updateComponentValue={updateComponentValue}
													allowImageVariables={allowImageVariables}
												/>
											</div>
										))}
									</div>
								) : (
									<div className="space-y-2">
										<p className="text-sm text-slate-600">
											This component type is not mapped yet. Edit the raw JSON payload below and click Apply.
										</p>
										<textarea
											disabled={!editable}
											className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
											rows={8}
											value={jsonDraft}
											onChange={(e) => setJsonDrafts((prev) => ({ ...prev, [componentKey]: e.target.value }))}
										/>
										<div className="flex items-center justify-between">
											{jsonErrors[componentKey] ? (
												<p className="text-xs text-rose-600">{jsonErrors[componentKey]}</p>
											) : (
												<p className="text-xs text-slate-500">Paste valid JSON for the component content.</p>
											)}
											{editable && (
												<button
													type="button"
													className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
													onClick={() => {
													try {
														const parsed = JSON.parse(jsonDraft);
														updateComponentValue(index, ["content"], parsed);
														setJsonErrors((prev) => {
															const next = { ...prev };
															delete next[componentKey];
															return next;
														});
													} catch (err: any) {
														setJsonErrors((prev) => ({
															...prev,
															[componentKey]: "Invalid JSON – please fix and try again."
														}));
													}
												}}
												>
													Apply JSON
												</button>
											)}
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				);
			})}
		</div>
	</div>
);

export default PageEditorComponentsPanel;
